"use client"

import * as React from "react"
import { GitBranchIcon, HomeIcon, ShieldCheckIcon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { WorkspaceStructureType } from "@ibada/shared-types/auth"
import {
  ChurchNameConflict,
  type ChurchNameCheck,
  ONBOARDING_COUNTRIES,
  OnboardingLanguage,
  OnboardingRole,
  onboardingCountry,
  resolveOnboardingDefaults,
} from "@ibada/shared-types/onboarding"
import {
  Button,
  Card,
  CardContent,
  HelperMessage,
  Input,
  Label,
  PhoneInput,
  type PhoneInputCountry,
  Select,
  cn,
} from "@ibada/ui"

import { useLocale, useTranslations } from "@/i18n"
import {
  MobileFormPhoneRow,
  MobileFormRow,
  MobileFormSection,
  MobileFormSelectRow,
} from "@/shared/components/mobile-form"
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@ibada/ui"
import { checkChurchName } from "../services/create-church-api"
import { CHURCH_NAME_MAX_LENGTH } from "../model/create-church-form"

/** The address the app already uses for human help (see the members import flow). */
const SUPPORT_EMAIL = "support@ibadacloud.com"

const LANGUAGE_NATIVE_LABEL: Record<OnboardingLanguage, string> = {
  [OnboardingLanguage.English]: "English",
  [OnboardingLanguage.French]: "Français",
  [OnboardingLanguage.Kinyarwanda]: "Kinyarwanda",
}

export type ChurchRegistrationValues = {
  name: string
  country: string
  language: OnboardingLanguage
  structureType: WorkspaceStructureType
  creatorRole: OnboardingRole
  phoneNumber: string
}

type ChurchRegistrationFormProps = {
  /**
   * The host renders the submit button, associating it with `<form form={formId}>`. Native form association
   * works across DOM boundaries, so a dialog can keep its action in `DialogFooter` and a page can keep its at
   * the foot of the card — without refs, imperative handles, or the form guessing where it lives.
   */
  formId: string
  onSubmit: (values: ChurchRegistrationValues) => Promise<void> | void
  onValidityChange?: (canSubmit: boolean) => void
  disabled?: boolean
  className?: string
  /** The phone form kit layout (the full page on a phone; the dialog host keeps the regular fields). */
  mobileLayout?: boolean
}

/**
 * The church details form, hosted two ways.
 *
 * A person with no workspace meets it as a full page — it is the only thing they can do. A person who already
 * has one meets it as a dialog from the workspace switcher, because adding a second church is an errand, not
 * an onboarding. Same fields, same validation, same request; only the chrome differs.
 *
 * Lifted out of the pre-auth wizard rather than rewritten, so the parts that were argued over — the dial code
 * following the country, the country NOT rewriting the language, the amber duplicate-name notice — survive
 * intact. There is no email field: the caller is authenticated, so we already know where they sign in.
 */
export function ChurchRegistrationForm({
  formId,
  onSubmit,
  onValidityChange,
  disabled = false,
  className,
  mobileLayout = false,
}: ChurchRegistrationFormProps) {
  const t = useTranslations("createChurch")

  /*
   * The language they are READING this form in outranks the one their browser was installed with.
   *
   * `userLanguage` is the "explicitly chosen" signal and sits above `browserLanguage` in the precedence order,
   * so a person who switched the app to French — on this very page, the switcher is in the corner — gets a
   * French church. Passing only the browser locale is how a church registered entirely in French ended up
   * `defaultLanguage: "en"`, and then handed its members English receipts.
   */
  const locale = useLocale()
  const initial = React.useMemo(
    () =>
      resolveOnboardingDefaults({
        userCountry: "COD",
        userLanguage: locale,
        browserLanguage: readBrowserLanguage(),
      }),
    [locale],
  )

  const [name, setName] = React.useState("")
  const [nameError, setNameError] = React.useState<string | null>(null)
  const [nameCheck, setNameCheck] = React.useState<ChurchNameCheck | null>(null)
  const [creatorRole, setCreatorRole] = React.useState<OnboardingRole | "">("")
  const [roleError, setRoleError] = React.useState<string | null>(null)
  const [country, setCountry] = React.useState(initial.country)
  const [language, setLanguage] = React.useState<OnboardingLanguage>(initial.language)
  /*
   * Follow the app's language until the field is touched. Switching the app to French while looking at this
   * form should move it; having chosen a language explicitly, nothing may move it again — the same rule the
   * country field already follows, and the reason the country deliberately does NOT rewrite this.
   */
  const [languageTouched, setLanguageTouched] = React.useState(false)
  React.useEffect(() => {
    if (languageTouched) return
    setLanguage(initial.language)
  }, [initial.language, languageTouched])
  const [structureType, setStructureType] = React.useState<WorkspaceStructureType>(
    WorkspaceStructureType.SingleBranch,
  )
  const [phoneNumber, setPhoneNumber] = React.useState("")
  const [phoneError, setPhoneError] = React.useState<string | null>(null)

  /** The country field is ISO-3; the phone input speaks ISO-2. DR Congo is the reference-market fallback. */
  const phoneCountryIso2 = (onboardingCountry(country)?.iso2 ?? "CD") as PhoneInputCountry

  /*
   * Which country the phone input was MOUNTED with — it only moves while the number is still empty.
   *
   * `react-phone-number-input` reads `defaultCountry` once, so adopting a new dial code means remounting. The
   * previous key ("entered" once anything was typed) remounted on the FIRST KEYSTROKE too: the library emits
   * E.164, so `phoneNumber` went from "" to "+2507", the key changed, and the fresh mount was handed a value
   * carrying its own calling code — which initialises the display in INTERNATIONAL mode. The field then read
   * "+250 7" next to a prefix already showing +250, and every later character landed inside that string.
   *
   * Keyed on this instead, the mount survives typing. A country change still remounts while the field is empty,
   * and stops doing so once a number exists — a typed number is more specific than the dropdown above it.
   */
  const [phoneInputCountry, setPhoneInputCountry] = React.useState<PhoneInputCountry>(phoneCountryIso2)
  React.useEffect(() => {
    if (phoneNumber.trim()) return
    setPhoneInputCountry(phoneCountryIso2)
  }, [phoneCountryIso2, phoneNumber])

  const canSubmit = name.trim().length >= 2 && Boolean(creatorRole) && phoneNumber.trim().length >= 8
  React.useEffect(() => {
    onValidityChange?.(canSubmit)
  }, [canSubmit, onValidityChange])

  /*
   * Whether a church is already registered under this name. A NOTICE, not a gate: church names repeat across
   * cities, so refusing the second "Église La Grâce" would turn a real signup away to protect against a
   * mistake. Debounced — someone typing a name is not asking a question on every keystroke.
   */
  React.useEffect(() => {
    const candidate = name.trim()
    if (candidate.length < 2) {
      setNameCheck(null)
      return
    }

    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        const result = await checkChurchName(candidate)
        if (!cancelled) setNameCheck(result.conflict === ChurchNameConflict.None ? null : result)
      } catch {
        // A courtesy lookup that fails says nothing — never warn on our own outage.
        if (!cancelled) setNameCheck(null)
      }
    }, 500)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [name])

  const countryOptions = React.useMemo(
    () => ONBOARDING_COUNTRIES.map((entry) => ({ value: entry.code, label: entry.name })),
    [],
  )
  const languageOptions = React.useMemo(
    () =>
      (Object.values(OnboardingLanguage) as OnboardingLanguage[]).map((value) => ({
        value,
        label: LANGUAGE_NATIVE_LABEL[value],
      })),
    [],
  )
  const roleOptions = React.useMemo(
    () =>
      (Object.values(OnboardingRole) as OnboardingRole[]).map((value) => ({
        value,
        label: t(`field.role.options.${value}`),
      })),
    [t],
  )

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = name.trim()
    let invalid = false
    if (trimmed.length < 2) {
      setNameError(t("field.name.required"))
      invalid = true
    }
    if (!creatorRole) {
      setRoleError(t("field.role.required"))
      invalid = true
    }
    const trimmedPhone = phoneNumber.trim()
    if (trimmedPhone.length < 8) {
      setPhoneError(t("field.phone.required"))
      invalid = true
    }
    if (invalid || !creatorRole) return

    try {
      await onSubmit({
        name: trimmed,
        country,
        language,
        structureType,
        creatorRole,
        phoneNumber: trimmedPhone,
      })
    } catch {
      toast.error(t("error.saveFailed"))
    }
  }

  if (mobileLayout) {
    const tMobile = (key: string) => t(`mobile.${key}` as never)
    const picker = { doneLabel: tMobile("done"), emptyLabel: tMobile("noResults") }
    const single = structureType === WorkspaceStructureType.SingleBranch
    return (
      <form id={formId} onSubmit={handleSubmit} className={cn("flex flex-col gap-5", className)} noValidate>
        <div>
          <MobileFormSection>
            <label className='flex min-h-[3.25rem] w-full min-w-0 items-center gap-3 px-4 text-base'>
              <span className={cn("shrink-0 font-semibold", nameError ? "text-destructive" : "text-foreground")}>{t("field.name.label")}</span>
              <input
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                  if (nameError) setNameError(null)
                }}
                placeholder={t("field.name.placeholder")}
                maxLength={CHURCH_NAME_MAX_LENGTH}
                disabled={disabled}
                autoCapitalize='words'
                className='min-w-0 flex-1 bg-transparent text-right text-base font-semibold text-foreground outline-none placeholder:text-muted-foreground/70'
              />
            </label>
          </MobileFormSection>
          <p className='flex items-start gap-1.5 px-4 pt-1.5 text-xs font-semibold text-muted-foreground'>
            <ShieldCheckIcon aria-hidden className='mt-px size-3.5 shrink-0 text-warning' />
            {t("field.name.verificationNotice")}
          </p>
          {nameError ? <p className='px-4 pt-1.5 text-[13px] font-semibold text-destructive'>{nameError}</p> : null}
          {!nameError && nameCheck ? (
            <div className='pt-3'>
              <NameConflictNotice check={nameCheck} typedName={name.trim()} />
            </div>
          ) : null}
        </div>

        <div>
          <MobileFormSection>
            <MobileFormSelectRow
              {...picker}
              label={tMobile("role")}
              placeholder={tMobile("choose")}
              value={creatorRole || undefined}
              onChange={(value) => {
                if (!value) return
                setCreatorRole(value as OnboardingRole)
                if (roleError) setRoleError(null)
              }}
              options={roleOptions.map((option) => ({ value: option.value, label: String(option.label) }))}
              sheetTitle={t("field.role.label")}
              invalid={Boolean(roleError)}
              disabled={disabled}
            />
            <MobileFormSelectRow
              {...picker}
              label={t("field.country.label")}
              placeholder={tMobile("choose")}
              value={country}
              onChange={(value) => value && setCountry(value)}
              options={countryOptions}
              searchPlaceholder={t("field.country.label")}
              disabled={disabled}
            />
            <MobileFormSelectRow
              {...picker}
              label={t("field.language.label")}
              placeholder={tMobile("choose")}
              value={language}
              onChange={(value) => {
                if (!value) return
                setLanguageTouched(true)
                setLanguage(value as OnboardingLanguage)
              }}
              options={languageOptions}
              disabled={disabled}
            />
          </MobileFormSection>
          {roleError ? <p className='px-4 pt-1.5 text-[13px] font-semibold text-destructive'>{roleError}</p> : null}
        </div>

        <div>
          <MobileFormSection title={t("field.phone.label")} footer={t("field.phone.help")}>
            {/* Remounted when the country changes, never while typing (see the desktop field below). */}
            <MobileFormPhoneRow
              key={phoneInputCountry}
              defaultCountry={phoneInputCountry}
              value={phoneNumber}
              onChange={(value) => {
                setPhoneNumber(value)
                if (phoneError) setPhoneError(null)
              }}
              placeholder={t("field.phone.placeholder")}
              disabled={disabled}
            />
          </MobileFormSection>
          {phoneError ? <p className='px-4 pt-1.5 text-[13px] font-semibold text-destructive'>{phoneError}</p> : null}
        </div>

        <StructureSection single={single} disabled={disabled} onChange={setStructureType} />
      </form>
    )
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className={cn("space-y-5", className)} noValidate>
      <div className="space-y-2">
        <Label htmlFor={`${formId}-name`}>{t("field.name.label")}</Label>
        <Input
          id={`${formId}-name`}
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            if (nameError) setNameError(null)
          }}
          placeholder={t("field.name.placeholder")}
          maxLength={CHURCH_NAME_MAX_LENGTH}
          showCount
          disabled={disabled}
        />
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ShieldCheckIcon aria-hidden className="mt-px size-3.5 shrink-0 text-warning" />
          {t("field.name.verificationNotice")}
        </p>
        {nameError ? <p className="text-sm text-destructive">{nameError}</p> : null}
        {!nameError && nameCheck ? (
          <NameConflictNotice check={nameCheck} typedName={name.trim()} />
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>{t("field.role.label")}</Label>
        <Select
          options={roleOptions}
          value={creatorRole || undefined}
          onValueChange={(value) => {
            setCreatorRole(value as OnboardingRole)
            if (roleError) setRoleError(null)
          }}
          placeholder={t("field.role.placeholder")}
          disabled={disabled}
        />
        {roleError ? <p className="text-sm text-destructive">{roleError}</p> : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t("field.country.label")}</Label>
          {/* The language is NOT touched. The country says where the church is, not what language its office
              works in, and rewriting the UI locale mid-form was disorienting. */}
          <Select
            options={countryOptions}
            value={country}
            onValueChange={setCountry}
            isSearchable
            disabled={disabled}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("field.language.label")}</Label>
          <Select
            options={languageOptions}
            value={language}
            onValueChange={(value) => {
              setLanguageTouched(true)
              setLanguage(value as OnboardingLanguage)
            }}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t("field.phone.label")}</Label>
        {/*
          The dial code follows the country. `defaultCountry` is read once and left uncontrolled — passing a
          controlled country too makes `react-phone-number-input` fall back to international formatting and
          leak the calling code into the number box — so remounting is how a new country is adopted. See
          `phoneInputCountry`: the key must never change because of TYPING, only because of the country.
        */}
        <PhoneInput
          key={phoneInputCountry}
          defaultCountry={phoneInputCountry}
          value={phoneNumber}
          onChange={(value) => {
            setPhoneNumber(value)
            if (phoneError) setPhoneError(null)
          }}
          placeholder={t("field.phone.placeholder")}
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">{t("field.phone.help")}</p>
        {phoneError ? <p className="text-sm text-destructive">{phoneError}</p> : null}
      </div>

      <div className="space-y-2">
        <Label>{t("field.structure.label")}</Label>
        <Card
          size="sm"
          className="border-primary/60 bg-primary/5 shadow-none"
          aria-live="polite"
          data-structure-type={structureType}
        >
          <CardContent className="flex items-center gap-3">
            <span className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-lg">
              {structureType === WorkspaceStructureType.SingleBranch ? (
                <HomeIcon className="size-4.5" />
              ) : (
                <GitBranchIcon className="size-4.5" />
              )}
            </span>
            <div className="min-w-0 space-y-0.5">
              <p className="font-semibold">
                {structureType === WorkspaceStructureType.SingleBranch
                  ? t("field.structure.single.title")
                  : t("field.structure.multi.title")}
              </p>
              <p className="text-muted-foreground text-sm">
                {structureType === WorkspaceStructureType.SingleBranch
                  ? t("field.structure.single.description")
                  : t("field.structure.multi.description")}
              </p>
            </div>
          </CardContent>
        </Card>
        <Button
          type="button"
          variant="link"
          size="sm"
                className="text-primary hover:text-primary/80 h-auto w-fit max-w-full cursor-pointer whitespace-normal break-words p-0 text-left text-xs leading-snug font-semibold underline underline-offset-4"
          disabled={disabled}
          onClick={() =>
            setStructureType(
              structureType === WorkspaceStructureType.SingleBranch
                ? WorkspaceStructureType.MultiBranch
                : WorkspaceStructureType.SingleBranch,
            )
          }
        >
          {structureType === WorkspaceStructureType.SingleBranch
            ? t("field.structure.switchToMulti")
            : t("field.structure.switchToSingle")}
        </Button>
      </div>
    </form>
  )
}

/**
 * The church's structure on a phone, with one parish as THE answer: it is stated, not offered as one of two
 * equal choices. Several parishes sits behind a small green question under it, and a sheet asks once more before
 * switching, because almost every church is one parish and picking the other by accident shapes the whole space.
 */
function StructureSection({
  single,
  disabled,
  onChange,
}: {
  single: boolean
  disabled: boolean
  onChange: (value: WorkspaceStructureType) => void
}) {
  const t = useTranslations("createChurch")
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const Icon = single ? HomeIcon : GitBranchIcon
  return (
    <div>
      <MobileFormSection title={t("field.structure.label")}>
        <MobileFormRow
          label={single ? t("field.structure.single.title") : t("field.structure.multi.title")}
          trailing={<Icon aria-hidden className='size-[1.125rem] shrink-0 text-primary' />}
        />
      </MobileFormSection>
      <div className='px-4 pt-1.5'>
        <button
          type='button'
          disabled={disabled}
          onClick={() => (single ? setConfirmOpen(true) : onChange(WorkspaceStructureType.SingleBranch))}
          className='-mx-1 flex items-center gap-1.5 rounded-full px-1 py-1 text-left text-[13px] font-bold text-primary active:bg-primary/10'
        >
          {single ? <GitBranchIcon aria-hidden className='size-3.5 shrink-0' /> : <HomeIcon aria-hidden className='size-3.5 shrink-0' />}
          {single ? t("field.structure.switchToMulti") : t("field.structure.switchToSingle")}
        </button>
      </div>
      <Drawer open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DrawerContent>
          <DrawerHeader className='group-data-[vaul-drawer-direction=bottom]/drawer-content:text-left'>
            <DrawerTitle className='text-lg font-extrabold'>{t("mobile.multiTitle")}</DrawerTitle>
            <DrawerDescription className='text-[15px] font-semibold text-muted-foreground'>{t("mobile.multiBody")}</DrawerDescription>
          </DrawerHeader>
          <div className='flex flex-col gap-2.5 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'>
            <button
              type='button'
              onClick={() => setConfirmOpen(false)}
              className='h-[3.25rem] rounded-full bg-primary text-base font-extrabold text-primary-foreground active:brightness-95'
            >
              {t("mobile.keepSingle")}
            </button>
            <button
              type='button'
              onClick={() => {
                onChange(WorkspaceStructureType.MultiBranch)
                setConfirmOpen(false)
              }}
              className='h-[3.25rem] rounded-full text-base font-bold text-muted-foreground active:bg-muted'
            >
              {t("mobile.chooseMulti")}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  )
}

/**
 * What to say when the typed name is already registered — and, crucially, what to OFFER.
 *
 * The old panel offered "sign in" to everyone, which is wrong for everyone: the person reading it is already
 * signed in, so it either bounced them straight back or promised access to a church that has never heard of
 * them. There are three genuinely different situations here and only the server can tell them apart, so the
 * conflict comes back resolved and this renders the one exit that actually exists.
 *
 * Amber in every case, not destructive: nothing is refused, and a red message that blocks nothing teaches
 * people to ignore red messages. Actions sit on a row of their own rather than inside the sentence — inline
 * they wrapped mid-phrase and the paragraph read as underlined fragments.
 */
function NameConflictNotice({ check, typedName }: { check: ChurchNameCheck; typedName: string }) {
  const t = useTranslations("createChurch.field.name")
  const navigate = useNavigate()
  /*
   * For a member or an invitee the server sends the church's real registered name; for a namesake it sends
   * nothing, so the only name available is the one they typed — which is exactly the name support needs.
   */
  const workspaceName = check.workspace?.name ?? typedName

  if (check.conflict === ChurchNameConflict.Member && check.workspace) {
    /* They are already inside. Creating a second church is not the fix — going to the first one is. */
    return (
      <HelperMessage tone="info" title={t("conflict.member.title")}>
        {t("conflict.member.body", { name: workspaceName })}
        <ConflictActions>
          <button
            type="button"
            className="cursor-pointer underline underline-offset-2"
            onClick={() => navigate(`/${check.workspace?.slug}`)}
          >
            {t("conflict.member.open", { name: workspaceName })}
          </button>
        </ConflictActions>
      </HelperMessage>
    )
  }

  if (check.conflict === ChurchNameConflict.Invited && check.invitationCode) {
    /*
     * The exact failure this whole post-auth flow exists to catch: invited, never saw the email, about to
     * register a duplicate of the church that already wants them. Straight to accept.
     */
    const params = new URLSearchParams({ code: check.invitationCode })
    if (check.workspace?.id) params.set("workspaceId", check.workspace.id)

    return (
      <HelperMessage tone="info" title={t("conflict.invited.title")}>
        {t("conflict.invited.body", { name: workspaceName })}
        <ConflictActions>
          <button
            type="button"
            className="cursor-pointer underline underline-offset-2"
            onClick={() => navigate(`/accept-invitation?${params.toString()}`)}
          >
            {t("conflict.invited.accept")}
          </button>
        </ConflictActions>
      </HelperMessage>
    )
  }

  /*
   * A namesake, and we know nothing more than that. No "sign in" — this account has no way into that church,
   * and offering one would be a dead end dressed as a solution. Continuing is legitimate here, so the copy
   * says so first; the only other real option is telling us if the name was taken by mistake.
   */
  /*
   * The name goes in the subject. Support used to receive "Le nom de mon église a été enregistré par quelqu'un
   * d'autre" with no church named anywhere — a ticket whose first reply had to be "which one?". The body is
   * left empty: what they want to say is theirs to write.
   */
  const mailto =
    `mailto:${SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(t("conflict.unrelated.mailSubject", { name: workspaceName }))}`

  return (
    <HelperMessage tone="warning" title={t("conflict.unrelated.title")}>
      {t("conflict.unrelated.body")}
      <ConflictActions>
        <a href={mailto} className="underline underline-offset-2">
          {t("conflict.unrelated.contact")}
        </a>
      </ConflictActions>
    </HelperMessage>
  )
}

function ConflictActions({ children }: { children: React.ReactNode }) {
  return <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 font-medium">{children}</div>
}

function readBrowserLanguage() {
  if (typeof navigator === "undefined") return null
  return navigator.language ?? null
}
